'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { forwardRef, type MouseEvent } from 'react';
import { useI18n } from '../../hooks/useI18n';

export interface CiteProps {
  /** Citation index (1-based). Rendered as `[n]`. */
  n: number;
  /**
   * Optional anchor target. When `onClick` isn't supplied and `sourceId` is,
   * Cite becomes a same-page anchor that scrolls to the source.
   */
  sourceId?: string;
  /**
   * Optional click handler. Receives the citation index for jump-to-source
   * behavior. When provided, Cite renders as a `<button>` element.
   */
  onClick?: (n: number) => void;
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

/**
 * `Cite` — superscript citation marker like `[1]`, `[2]`, anchored to a source.
 *
 * Inline, 11px (overline variant), with `letterSpacing >= 0.08em` per the
 * design-system typography rule (§11b2). Clickable to scroll to a source
 * panel; when no handler is provided it renders as a non-interactive span.
 */
export const Cite = forwardRef<HTMLElement, CiteProps>(function Cite({ n, sourceId, onClick, sx }, ref) {
  const { t } = useI18n();
  const interactive = Boolean(onClick) || Boolean(sourceId);
  const ariaLabel = t('designSystem.citations.markerAriaLabel', { n });

  // Decide element based on interaction surface. href takes precedence over
  // onClick when only a sourceId is provided so deep-linking still works
  // without JS.
  const component: 'a' | 'button' | 'span' = onClick ? 'button' : sourceId ? 'a' : 'span';

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (onClick) {
      event.preventDefault();
      onClick(n);
    }
  };

  // Base styles — keep size at overline (11px) and ensure letter-spacing
  // meets the 0.08em minimum from §11b2. We don't go below 11px anywhere.
  const baseSx = (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'super',
    lineHeight: 1,
    mx: '1px',
    px: 0.4,
    py: 0,
    minWidth: 14,
    height: 14,
    borderRadius: '3px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
    border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.2)}`,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    cursor: interactive ? 'pointer' : 'default',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    ...(interactive && {
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.14),
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 1,
      },
    }),
  });

  const sxArray: SxProps<Theme> = sx == null ? baseSx : [baseSx, ...(Array.isArray(sx) ? sx : [sx])];

  return (
    <Box
      ref={ref}
      component={component as 'span'}
      {...(component === 'a' ? { href: `#${sourceId}` } : {})}
      {...(component === 'button' ? { type: 'button' as const, onClick: handleClick } : {})}
      aria-label={ariaLabel}
      sx={sxArray}
    >
      <Typography
        component="span"
        variant="overline"
        sx={{
          // Override overline's default block-ish line-height so the marker
          // sits tight inside the pill. The 0.1em letter-spacing already
          // carried by overline exceeds the 0.08em floor.
          lineHeight: 1,
          fontWeight: 'inherit',
          letterSpacing: 'inherit',
          color: 'inherit',
        }}
      >
        [{n}]
      </Typography>
    </Box>
  );
});

Cite.displayName = 'Cite';

export default Cite;
